import {
  AfterViewChecked,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { Event, NavigationEnd, Router, RouterLink } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { DsButtonVariants, DsModule, Icons } from '@levelaccess/design-system';

import { AccessibilityAuditToolNames } from '@shared/constants/audit-tool';
import { $flaw } from '@shared/constants/flaw';
import { IAutomatedScanIssue, IScanIssues } from '@shared/interfaces/scan-issues.interface';
import { IIssueStatusHistory } from '@shared/interfaces/issue-status-history.interface';
import { Api } from '@shared/constants/api';
import { $task } from '@shared/constants/task';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { RequiredSecurities } from '@shared/constants/required-securities';

import { CommonPipesModule } from '@client/app/shared/common-pipes.module';
import { SanitizerPipe } from '@client/app/pipes/sanitizer.pipe';
import { HighlightDirective } from '@client/app/directives/highlight/highlight.directive';
import {
  FINDING_CRITERIA_AND_STANDARD_LABEL_KEY,
  FindingChipService,
} from '@client/app/services/issue-finding-details/finding-chip.service';
import { IFindingChipConfig, IFindingChipGroup, IFindingContentBlock } from '@client/app/interfaces/finding.interface';
import { FindingMetadataCardComponent } from '@client/app/pages/public/scans/scan-results/issues-view-details-base/finding-metadata-card/finding-metadata-card.component';
import { ScanReportService } from '@client/app/services/scan-report.service';
import { IssueStatusHistoryService } from '@client/app/services/issue-status-history.service';
import { ViewFlawStatusHistoryItemComponent } from '@client/app/components/view-flaw-status-history/view-flaw-status-history-item/view-flaw-status-history-item.component';
import { AclSecurityAdapter } from '@client/app/services/acl.service';
import { UserAclService } from '@client/app/services/user-acl.service';

@Component({
  selector: 'app-issues-view-details-base',
  templateUrl: './issues-view-details-base.component.html',
  styleUrl: './issues-view-details-base.component.scss',
  standalone: true,
  imports: [
    DsModule,
    CommonPipesModule,
    SanitizerPipe,
    HighlightDirective,
    FindingMetadataCardComponent,
    RouterLink,
    AsyncPipe,
    ViewFlawStatusHistoryItemComponent,
  ],
})
export class IssuesViewDetailsBaseComponent implements OnInit, AfterViewChecked, OnDestroy {
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;
  public readonly $task: typeof $task = $task;

  public issue: InputSignal<IAutomatedScanIssue> = input<IAutomatedScanIssue>();

  public scanId: InputSignal<string> = input<string>();

  public ruleDetails: InputSignal<IScanIssues> = input<IScanIssues>();

  public toolName: InputSignal<AccessibilityAuditToolNames | null> = input<AccessibilityAuditToolNames | null>(null);

  public whereIsItLocated: InputSignal<string> = input<string>();

  public whereIsItLocatedUrl: InputSignal<string | null> = input<string | null>(null);

  public mainContentBlocks: InputSignal<IFindingContentBlock[]> = input<IFindingContentBlock[]>([]);

  @Output()
  public dismissFinding: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public restoreFinding: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public createTask: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public navigatedAway: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('taskLinkElement')
  private taskLinkElement?: ElementRef<HTMLAnchorElement>;

  private subscriptions: Subscription = new Subscription();
  private focusedTaskId?: string;

  public chips: Signal<IFindingChipGroup[]> = computed((): IFindingChipGroup[] =>
    this.findingChipService.buildChips(this.issue(), this.ruleDetails(), this.toolName()),
  );

  public metadataChipGroups: Signal<IFindingChipGroup[]> = computed((): IFindingChipGroup[] =>
    this.chips().filter((group: IFindingChipGroup) => group.labelKey !== FINDING_CRITERIA_AND_STANDARD_LABEL_KEY),
  );

  public criteriaAndStandardLinks: Signal<IFindingChipConfig[]> = computed(
    (): IFindingChipConfig[] =>
      this.chips().find((group: IFindingChipGroup) => group.labelKey === FINDING_CRITERIA_AND_STANDARD_LABEL_KEY)?.chips ?? [],
  );

  public issueStatusHistories$: Observable<IIssueStatusHistory[]> = toObservable(this.issue).pipe(
    switchMap((issue: IAutomatedScanIssue) =>
      this.scanReportService.hasHistory(issue) ? this.issueStatusHistoryService.getStatusUpdateHistory(issue[$flaw._id]) : of([]),
    ),
    catchError(() => of([])),
  );

  public hasDismissFindingRequiredSecurity$: Observable<boolean>;

  constructor(
    private findingChipService: FindingChipService,
    private router: Router,
    private scanReportService: ScanReportService,
    private issueStatusHistoryService: IssueStatusHistoryService,
    private userAclService: UserAclService,
  ) {
    this.hasDismissFindingRequiredSecurity$ = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useWorkspaceFromUser()
            .useDigitalPropertyFromUser()
            .useFunctionalActions(RequiredSecurities.AT_Scans_Update.functionalActions)
            .check(),
        ),
      );
  }

  public isDismissed(): boolean {
    return this.scanReportService.isDismissed(this.issue());
  }

  public hasHistory(): boolean {
    return this.scanReportService.hasHistory(this.issue());
  }

  public get taskLink(): string {
    return this.issue().task
      ? `/${Api.projects}/${this.issue().task[$task.projectId]}/${Api.tasks}/${this.issue().task[$task._id]}`
      : '';
  }

  public ngOnInit(): void {
    this.focusedTaskId = this.issue()?.task?.[$task._id];

    let lastPathname: string = this.getPathname(this.router.url);
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd): void => {
          const nextPathname: string = this.getPathname(event.urlAfterRedirects);
          if (nextPathname === lastPathname) {
            return;
          }

          lastPathname = nextPathname;
          this.navigatedAway.emit();
        }),
    );
  }

  public ngAfterViewChecked(): void {
    const taskId: string | undefined = this.issue()?.task?.[$task._id];

    if (
      SharedCommonUtility.notNullish(taskId) &&
      taskId !== this.focusedTaskId &&
      SharedCommonUtility.notNullish(this.taskLinkElement)
    ) {
      this.focusedTaskId = taskId;
      this.taskLinkElement.nativeElement.focus();
    }
  }

  private getPathname(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
